import * as React from 'react';

import {TextField} from 'shells/textfield/textfield';

import {useGetTeams} from '../../core/graphql/hooks/use-get-teams';
import type {ContentType} from '../../timeline/creator/timeline-creator';

import {CoreEntityCreatorForm} from './core-entity-creator-form';

export type CreateTimelineEventFormProps = {
    entityId: string;
    entityName: string;
    contentType: ContentType;
    showPlaceholder?: boolean;
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onBlur?: () => void;
    onAfterSubmit?: (id: string, activityTypeName?: string, noteBody?: string) => void;
    onFocus?: () => void;
};

export function CreateTimelineEventForm(props: CreateTimelineEventFormProps) {
    const {contentType, showPlaceholder = true, isOpen, onOpenChange, onBlur, onFocus} = props;
    const {teams} = useGetTeams();

    const [isForceOpen, setIsForceOpen] = React.useState<boolean>(false);
    const [hasNoteValue, setHasNoteValue] = React.useState<boolean>(false);
    const [hasActivityFormDataChanged, setHasActivityFormDataChanged] =
        React.useState<boolean>(false);
    const [shouldAutoFocusEditor, setShouldAutoFocusEditor] = React.useState<boolean>(false);
    const formRef = React.useRef<HTMLDivElement | null | undefined>(null);

    const handleClose = React.useCallback(() => {
        onOpenChange(false);
    }, [onOpenChange]);

    const handleBlur = React.useCallback(() => {
        onOpenChange(false);
        if (onBlur) {
            onBlur();
        }
    }, [onOpenChange, onBlur]);

    React.useEffect(() => {
        const isActivity = contentType === 'ACTIVITY';
        setIsForceOpen(hasNoteValue || (isActivity && hasActivityFormDataChanged));
    }, [hasNoteValue, hasActivityFormDataChanged, contentType]);

    React.useEffect(() => {
        if (isOpen) {
            setShouldAutoFocusEditor(true);

            requestAnimationFrame(() => {
                setShouldAutoFocusEditor(false);
            });
        }
    }, [isOpen]);

    React.useEffect(() => {
        const handleClickOutsideForm = (event: MouseEvent) => {
            if (
                formRef.current &&
                event.target instanceof Node &&
                !formRef.current.contains(event.target)
            ) {
                if (!isForceOpen) {
                    handleBlur();
                }
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (!isForceOpen) {
                    handleBlur();
                }
            }
        };

        if (isOpen) {
            document.addEventListener('mouseup', handleClickOutsideForm);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mouseup', handleClickOutsideForm);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, isForceOpen, handleBlur]);

    const placeholder =
        props.contentType === 'ACTIVITY'
            ? 'Log what happened in your activity… @Mention other users to grab their attention, or reference other companies, people, or leads.'
            : 'Write a note… @Mention other users to grab their attention, or reference other companies, people, and leads.';

    if (isOpen) {
        return (
            // @ts-expect-error
            <div ref={formRef}>
                <CoreEntityCreatorForm
                    entityId={props.entityId}
                    entityName={props.entityName}
                    refetchEvents={() => {
                        return Timeline.refreshTimeline();
                    }}
                    variant='lead-page'
                    contentType={contentType}
                    shouldAutoFocusEditor={shouldAutoFocusEditor}
                    preloadedTeams={teams}
                    onClose={handleClose}
                    isFullWidth={true}
                    setHasNoteValue={setHasNoteValue}
                    setHasActivityFormDataChanged={setHasActivityFormDataChanged}
                    onAfterSubmit={props.onAfterSubmit}
                    onFocus={onFocus}
                />
            </div>
        );
    }

    if (showPlaceholder) {
        return (
            // @ts-expect-error
            <div ref={formRef}>
                <TextField
                    placeholder={placeholder}
                    onFocus={() => {
                        onOpenChange(true);
                    }}
                />
            </div>
        );
    }

    return null;
}
